import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import {
  useGetConnectionFlowsSummary,
  useGetConnectionStats,
  useGetHealthTimeline,
} from "@features/ix/erpIntegration/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { RotateCcw, Check, AlertCircle, Code, Eye } from "lucide-react";
import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line } from "recharts";

interface SyncFlowRow {
  id: string;
  flow: string;
  direction: string;
  type: string;
  lastRun: string;
  success: string;
}

export const OverviewTab: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: statsData } = useGetConnectionStats(id, "24h");
  const { data: flowsSummaryData } = useGetConnectionFlowsSummary(id);
  const { data: healthTimelineData } = useGetHealthTimeline(id);

  const stats = [
    {
      title: "Records synced (24h)",
      displayValue: statsData?.recordsSynced?.value,
      icon: <RotateCcw className="h-6 w-6 text-[#5850ec]" />,
    },
    {
      title: "Success rate (24h)",
      displayValue: statsData?.successRate?.value,
      icon: <Check className="h-6 w-6 text-emerald-500" />,
    },
    {
      title: "Failed records",
      displayValue: statsData?.failedRecords?.value,
      icon: <AlertCircle className="h-6 w-6 text-red-500" />,
    },
    {
      title: "API calls (24h)",
      displayValue: statsData?.apiCalls?.value,
      icon: <Code className="h-6 w-6 text-[#0f766e]" />,
    },
  ];

  // Table columns definition using GenericTable format
  const columns = useMemo<TableColumn<SyncFlowRow>[]>(
    () => [
      {
        header: "Flow",
        key: "flow",
      },
      {
        header: "Direction",
        key: "direction",
        render: (_val, row) => (
          <Badge
            variant="outline"
            className="text-[10px] font-bold border border-gray-200 text-gray-500 rounded bg-white px-2 py-0.5"
          >
            {row.direction || "N/A"}
          </Badge>
        ),
      },
      {
        header: "Type",
        key: "syncType",
        render: (val, row) => {
          const isRealTime = val === "Real-time";
          return (
            <Badge
              className={`text-[10px] font-bold py-0.5 px-2.5 rounded-full border ${
                isRealTime
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50"
              }`}
            >
              {isRealTime && (
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5 inline-block shrink-0" />
              )}
              {val || "N/A"}
            </Badge>
          );
        },
      },
      {
        header: "Last Run",
        key: "lastRunAt",
      },
      {
        header: "Success",
        key: "successRate",
      },
    ],
    [],
  );

  // Recharts latency data for the sparkline wave
  const latencyData = [
    { value: 200 },
    { value: 215 },
    { value: 205 },
    { value: 220 },
    { value: 245 },
    { value: 235 },
    { value: 255 },
    { value: 210 },
    { value: 230 },
    { value: 224 },
  ];

  return (
    <div className="space-y-6">
      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((card, idx) => (
          <QuickStatsCard
            key={idx}
            title={card.title}
            count={card.displayValue}
            icon={card.icon}
          />
        ))}
      </div>

      <div className="grid grid-cols-12 gap-6 items-start">
        {/* Left column: Sync flows table */}
        <div className="col-span-12 lg:col-span-8 bg-white border border-gray-200 rounded-2xl shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900">Sync flows</h3>
          </div>

          <div className="overflow-hidden">
            <GenericTable
              data={flowsSummaryData || []}
              columns={columns}
              rowKey="id"
              actionsType="icons"
            />
          </div>
        </div>

        {/* Right column: Health timeline */}
        <div className="col-span-12 lg:col-span-4 bg-white border border-gray-200 rounded-2xl shadow-sm p-6 space-y-6">
          {/* Health Timeline Section */}
          <div className="space-y-3">
            <h3 className="text-lg font-bold text-gray-900">Health timeline</h3>
            <div className="text-[10px] font-bold text-gray-400 tracking-wider uppercase">
              LAST 24 HOURS
            </div>

            {/* Timeline Blocks */}
            <div className="flex flex-wrap gap-[3.5px] items-center min-h-[20px]">
              {healthTimelineData?.buckets?.length > 0 ? (
                healthTimelineData.buckets.map((bucket: any, idx: number) => (
                  <div
                    key={idx}
                    className={`w-2.5 h-5 rounded-[2px] ${
                      bucket.colour === "green"
                        ? "bg-emerald-500"
                        : bucket.colour === "amber" ||
                            bucket.colour === "orange"
                          ? "bg-amber-500"
                          : "bg-red-500"
                    }`}
                  />
                ))
              ) : (
                <div className="text-xs text-gray-400 font-medium">
                  No timeline data available
                </div>
              )}
            </div>

            <div className="flex justify-between text-xs text-gray-400 font-medium">
              <span>-24h</span>
              <span>now</span>
            </div>
          </div>

          {/* Average API Latency Section */}
          <div className="space-y-3 pt-4 border-t border-gray-100">
            <div className="text-[10px] font-bold text-gray-400 tracking-wider uppercase">
              AVG API LATENCY
            </div>

            {/* Recharts Latency Sparkline */}
            <div className="h-16 w-full">
              {latencyData?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={latencyData}
                    margin={{ top: 2, right: 2, left: 2, bottom: 2 }}
                  >
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#5850ec"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="w-full h-full flex items-center text-xs text-gray-400 font-medium">
                  No latency data available
                </div>
              )}
            </div>

            <div className="flex justify-between text-xs text-gray-400 font-medium">
              <span>p50: {healthTimelineData?.avgLatencyP50Ms ?? 0}ms</span>
              <span>p95: {healthTimelineData?.avgLatencyP95Ms ?? 0}ms</span>
            </div>
          </div>

          {/* Connection Status Section */}
          <div className="space-y-2 pt-4 border-t border-gray-100">
            <div className="text-[10px] font-bold text-gray-400 tracking-wider uppercase">
              CONNECTION STATUS
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`w-2 h-2 rounded-full animate-pulse ${
                  healthTimelineData?.connectionHealthy !== false
                    ? "bg-emerald-500"
                    : "bg-red-500"
                }`}
              />
              <span className="font-extrabold text-gray-900 text-sm">
                {healthTimelineData?.connectionHealthy !== false
                  ? "Healthy"
                  : "Unhealthy"}
              </span>
            </div>
            <div className="text-xs text-gray-400 font-medium">
              All endpoints reachable · last check 12s ago
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
